import { DISCLOSURE_BUTTON_CLASS } from './Constants';
import type { SmartMenusInstance } from './Types';

/**
 * Get the CDN-loaded jQuery instance via globalThis, bypassing Vite's inject plugin.
 * This is necessary because SmartMenus attaches its plugin methods (dataSM, smartmenus(),
 * $.SmartMenus) to the specific jQuery instance loaded from CDN (window.jQuery).
 * Importing $ from 'jquery' would give a separate Vite-bundled instance that
 * does not have these plugin methods on it.
 */
export const getJQuery = (): JQueryStatic => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = globalThis as any;
    const jq = win.jQuery || win.$;
    if (!jq) {
        throw new Error('jQuery not available on globalThis');
    }
    return jq as JQueryStatic;
};

/**
 * Get the SmartMenus instance from a menu container element.
 */
export const getSmartMenusInstance = ($element: JQuery): SmartMenusInstance | undefined => {
    const $menu = $element.closest('[data-smartmenus-id]');
    return $menu.data('smartmenus') as SmartMenusInstance | undefined;
};

/**
 * Find the associated link for a disclosure button.
 * Uses SmartMenus dataSM('parent-a') pattern, falls back to DOM traversal.
 */
export const findAssociatedLink = ($button: JQuery): JQuery | undefined => {
    const $li = $button.closest('li');
    const $sub = $li.children('ul.dropdown-menu').first();

    if (!$sub.length) {
        return $button.siblings('a').first();
    }

    let $link = $sub.dataSM('parent-a') as JQuery | undefined;
    if (!$link || !$link.length) {
        $link = $li.children('a').first();
    }
    return $link;
};

/**
 * Find the disclosure button associated with a submenu.
 * Reverse lookup: given a <ul>, find the sibling button that controls it.
 */
export const findButtonForSubmenu = ($sub: JQuery): JQuery => {
    const subId = $sub.attr('id');
    if (subId) {
        return $sub.siblings(`button[aria-controls="${subId}"]`);
    }
    return $sub.siblings(`.${DISCLOSURE_BUTTON_CLASS}`);
};

/**
 * CSS class applied to `.top-nav` by closeDropdown (KeyboardNavigation.ts) when
 * the Escape key dismisses a showcase while the pointer is still hovering over
 * the item. The SCSS rule for this class overrides the `:hover` show rule so the
 * menu stays hidden. Cleared by clearShowcaseDismissClass on any re-show path.
 */
export const SHOWCASE_DISMISSED_CLASS = 'ss-showcase-dismissed';

/**
 * Check whether a submenu <ul> is the outer layout wrapper for a showcase
 * mega-menu. Used in DisclosureButtons.ts and KeyboardNavigation.ts so a
 * single class-name string is the source of truth for the predicate.
 */
export const isShowcaseWrapper = ($ul: JQuery): boolean => $ul.hasClass('ss-dropdown-menu-with-showcase');

/**
 * Remove the Esc-dismissal class from the .top-nav that owns the showcase
 * wrapper, restoring normal CSS-driven visibility. Called on every legitimate
 * re-show or focus-out reset path so the dismiss state does not persist beyond
 * the next user interaction.
 */
export const clearShowcaseDismissClass = ($sub: JQuery): void => {
    $sub.closest('li.top-nav').removeClass(SHOWCASE_DISMISSED_CLASS);
};

/**
 * Get link text from an anchor element for use in aria-label.
 */
export const getLinkText = ($link: JQuery): string => {
    const $span = $link.find('span').first();
    return ($span.length ? $span.text() : $link.text()).trim();
};
